import Seo from "@/components/Seo";

/**
 * NOTE FOR DEVELOPERS:
 * Legal pages are starter content only and should be reviewed by a qualified
 * legal professional before launch.
 */
export default function Privacy() {
    return (
        <div data-testid="page-privacy">
            <Seo
                title="Privacy Policy | Chief AI Partners"
                description="How Chief AI Partners collects, uses and protects personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles."
                path="/privacy"
            />

            <section className="cap-section pt-24 md:pt-32 pb-12">
                <div className="cap-container">
                    <div className="cap-eyebrow mb-6">Legal</div>
                    <h1 className="font-serif text-navy text-5xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl">
                        Privacy Policy
                    </h1>
                    <p className="text-slate text-sm mt-4">Last updated: April 2026</p>
                </div>
            </section>

            <section className="pb-24">
                <div className="cap-container max-w-3xl text-slate leading-relaxed space-y-10">
                    <p className="text-base md:text-lg">
                        Chief AI Partners (a business operated by Sanlux Pty Ltd) respects your privacy and is committed to protecting your personal information.
                    </p>
                    <p className="text-base md:text-lg">
                        This policy explains how we collect, use and manage your information in accordance with the <em>Privacy Act 1988</em> (Cth) and the Australian Privacy Principles (APPs).
                    </p>

                    <LegalBlock number="1" title="Information we collect">
                        <p>We may collect personal information that you provide directly to us, including:</p>
                        <BulletList
                            items={[
                                "Name",
                                "Business email address",
                                "Phone number",
                                "Company name and role",
                                "Information included in enquiry or booking forms",
                            ]}
                        />
                        <p>We may also collect limited technical information automatically, such as:</p>
                        <BulletList
                            items={[
                                "IP address",
                                "Browser type",
                                "Device information",
                                "Pages visited on our website",
                            ]}
                        />
                    </LegalBlock>

                    <LegalBlock number="2" title="How we use your information">
                        <p>We use personal information to:</p>
                        <BulletList
                            items={[
                                "Respond to enquiries and booking requests",
                                "Deliver our services",
                                "Communicate with you where relevant to your enquiry",
                                "Send occasional updates if you have opted in",
                                "Improve our website and services",
                            ]}
                        />
                        <p>We only use your information where we have a lawful basis to do so.</p>
                    </LegalBlock>

                    <LegalBlock number="3" title="Contact and enquiry forms">
                        <p>When you submit a form on our website, your information is stored securely and used to respond to your request.</p>
                        <p>We do not sell or rent your personal information to third parties.</p>
                    </LegalBlock>

                    <LegalBlock number="4" title="Newsletter subscriptions">
                        <p>If you subscribe to our newsletter, we store your email address and any optional details you provide.</p>
                        <p>You can unsubscribe at any time using the link in our emails.</p>
                    </LegalBlock>

                    <LegalBlock number="5" title="Cookies and analytics">
                        <p>We use cookies to operate our website and improve performance.</p>
                        <BulletList
                            items={[
                                "Essential cookies are always active",
                                "Analytics cookies are only used with your consent",
                            ]}
                        />
                        <p>You can manage your cookie preferences through our cookie banner.</p>
                    </LegalBlock>

                    <LegalBlock number="6" title="Third-party services">
                        <p>We may use reputable third-party providers to support our operations, including:</p>
                        <BulletList
                            items={[
                                "Website hosting",
                                "Analytics tools",
                                "Email communication",
                            ]}
                        />
                        <p>These providers may process data on our behalf and are required to handle it in accordance with privacy and security standards.</p>
                    </LegalBlock>

                    <LegalBlock number="7" title="Storage and security">
                        <p>We take reasonable steps to protect personal information from misuse, loss, unauthorised access or disclosure.</p>
                        <p>Information may be stored using secure cloud services, which may be located within or outside Australia.</p>
                    </LegalBlock>

                    <LegalBlock number="8" title="Access and correction">
                        <p>You may request access to, or correction of, the personal information we hold about you.</p>
                        <p>
                            Requests can be made by contacting{" "}
                            <a href="mailto:enquiries@chiefai.partners" className="underline text-navy hover:text-teal">
                                enquiries@chiefai.partners
                            </a>.
                        </p>
                    </LegalBlock>

                    <LegalBlock number="9" title="Complaints">
                        <p>If you have a concern about how your information has been handled, please contact us first so we can resolve the issue.</p>
                        <p>If you are not satisfied, you may contact the Office of the Australian Information Commissioner (OAIC).</p>
                    </LegalBlock>

                    <LegalBlock number="10" title="Contact">
                        <p>For any questions about this Privacy Policy or how we handle your information, contact:</p>
                        <div className="mt-3 space-y-1 text-navy">
                            <div className="font-serif text-xl">Chief AI Partners</div>
                            <div>Sanlux Pty Ltd</div>
                            <div>Sydney, Australia</div>
                            <a href="mailto:enquiries@chiefai.partners" className="underline hover:text-teal">
                                enquiries@chiefai.partners
                            </a>
                        </div>
                    </LegalBlock>
                </div>
            </section>
        </div>
    );
}

function LegalBlock({ number, title, children }) {
    return (
        <div data-testid={`privacy-section-${number}`}>
            <div className="flex items-baseline gap-4 mb-4">
                <span className="text-xs uppercase tracking-[0.22em] text-teal font-semibold">
                    {number}
                </span>
                <h2 className="font-serif text-navy text-2xl md:text-3xl leading-tight">
                    {title}
                </h2>
            </div>
            <div className="space-y-4 text-base md:text-lg">{children}</div>
        </div>
    );
}

function BulletList({ items }) {
    return (
        <ul className="list-disc pl-6 space-y-1.5">
            {items.map((t) => (
                <li key={t}>{t}</li>
            ))}
        </ul>
    );
}
